"use client";

import { Check, MapPin, Video } from "lucide-react";
import { useBooking } from "../context";
import { durationMinutes, hasStarted, spotsState, type ScheduleSession } from "../schedule";
import { initials } from "../theme";

/** What the card's button does. The host app decides from the session and the visitor. */
export type ClassAction =
  /** `onBook` opens one-tap booking instead; the link stays for new tabs and no-JS. */
  | { kind: "book"; href: string; onBook?: () => void }
  | { kind: "waitlist"; href?: string; onJoin?: () => void; pending?: boolean }
  | { kind: "booked" }
  | { kind: "none" };

const focusRing =
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-studio-ink";
const pill =
  "inline-flex h-11 shrink-0 items-center justify-center whitespace-nowrap rounded-pill px-5 text-[15px] font-semibold transition-colors duration-150";

export function ClassCard({
  session,
  action,
  showColor = false,
}: {
  session: ScheduleSession;
  action: ClassAction;
  /** Class type colour dot before the title (the widget). */
  showColor?: boolean;
}) {
  const { t, fmt } = useBooking();
  const time = fmt.time(session.startTime);
  const day = fmt.dayLong(session.startTime);
  // Only when the host also asks for started classes (the widget): greyed out,
  // without spots or button.
  const started = hasStarted(session);
  // Cancelled by a studio closure: struck through, the closure's name instead
  // of spots and button.
  const closed = session.closedFor != null;

  return (
    <article
      className={`flex items-start gap-4 rounded-card border border-line bg-surface p-4 md:items-center md:gap-6 md:px-5 ${started ? "opacity-60" : ""}`}
    >
      <div className="w-14 shrink-0 tabular-nums">
        <p className="text-base font-semibold text-ink">{time}</p>
        <p className="text-[13px] text-ashe">
          {t("schedule.duration", { minutes: durationMinutes(session) })}
        </p>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <h3 className="flex items-center gap-2 text-base font-semibold text-ink">
          {showColor && <ClassDot color={session.color} />}
          <span className={closed ? "text-ink-soft line-through" : ""}>{session.title}</span>
        </h3>
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-ink-soft">
          {session.teacherName && (
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="flex size-5 items-center justify-center rounded-pill bg-studio-accent-soft text-[10px] font-semibold text-studio-ink"
              >
                {initials(session.teacherName).slice(0, 1)}
              </span>
              {t("schedule.withTeacher", { name: session.teacherName })}
            </span>
          )}
          {session.isOnline ? (
            <span className="flex items-center gap-1">
              <Video size={14} aria-hidden="true" />
              {t("schedule.online")}
            </span>
          ) : (
            session.room && (
              <span className="flex items-center gap-1">
                <MapPin size={14} aria-hidden="true" />
                {session.room}
              </span>
            )
          )}
        </p>
        {!started && !closed && <SpotsLabel session={session} className="md:hidden" />}
      </div>

      {!started && !closed && <SpotsLabel session={session} className="hidden w-48 md:block" />}

      {closed ? (
        <span className="shrink-0 text-[13px] font-medium text-warning-600">
          {session.closedFor
            ? t("schedule.closed", { name: session.closedFor })
            : t("schedule.closedNoName")}
        </span>
      ) : started ? (
        <span className="shrink-0 text-[13px] font-medium text-ashe">{t("schedule.ended")}</span>
      ) : (
      <div className="flex shrink-0 flex-col items-end gap-2 md:flex-row md:items-center md:gap-6">
        <Price session={session} />
        <ActionButton
          action={action}
          label={
            action.kind === "waitlist"
              ? t("schedule.waitlistLabel", { title: session.title, day, time })
              : t("schedule.bookLabel", { title: session.title, day, time })
          }
        />
      </div>
      )}
    </article>
  );
}

/** Class type colour, before a class name or a filter. */
export function ClassDot({ color }: { color: string | null | undefined }) {
  if (!color) return null;
  return (
    <span
      aria-hidden="true"
      className="inline-block size-2 shrink-0 rounded-pill"
      style={{ backgroundColor: color }}
    />
  );
}

function SpotsLabel({ session, className }: { session: ScheduleSession; className: string }) {
  const { t } = useBooking();
  const state = spotsState(session);

  if (state.kind === "open") return null;
  if (state.kind === "full") {
    return (
      <p className={`text-[13px] font-medium text-ink-soft ${className}`}>
        {state.waitlist > 0
          ? t("schedule.fullWaitlist", { count: state.waitlist })
          : t("schedule.full")}
      </p>
    );
  }
  return (
    <p
      className={`text-[13px] ${
        state.kind === "few" ? "font-medium text-warning-600" : "text-ashe"
      } ${className}`}
    >
      {t("schedule.spotsLeft", { count: state.count })}
    </p>
  );
}

function Price({ session }: { session: ScheduleSession }) {
  const { t, fmt } = useBooking();
  if (session.price === null) return null;

  const label = session.price === 0 ? t("schedule.free") : fmt.price(session.price);
  return (
    <span className="text-[15px] font-semibold tabular-nums text-ink md:w-24 md:text-right">
      {session.priceFrom ? t("schedule.priceFrom", { price: label }) : label}
    </span>
  );
}

function ActionButton({ action, label }: { action: ClassAction; label: string }) {
  const { t, Link } = useBooking();

  switch (action.kind) {
    case "book":
      return (
        <Link
          href={action.href}
          aria-label={label}
          onClick={
            action.onBook
              ? (event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                  event.preventDefault();
                  action.onBook!();
                }
              : undefined
          }
          className={`${pill} ${focusRing} bg-studio-accent text-studio-on-accent hover:opacity-90`}
        >
          {t("schedule.book")}
        </Link>
      );
    case "waitlist": {
      const className = `${pill} ${focusRing} border border-line bg-surface text-ink hover:border-line-strong`;
      if (action.href) {
        return (
          <Link href={action.href} aria-label={label} className={className}>
            {t("schedule.joinWaitlist")}
          </Link>
        );
      }
      return (
        <button
          type="button"
          onClick={action.onJoin}
          disabled={action.pending}
          aria-busy={action.pending || undefined}
          aria-label={label}
          className={`${className} disabled:opacity-60`}
        >
          {t("schedule.joinWaitlist")}
        </button>
      );
    }
    case "booked":
      return (
        <span className="inline-flex h-11 items-center gap-1.5 rounded-pill bg-success-50 px-4 text-sm font-semibold text-success-700">
          <Check size={16} aria-hidden="true" />
          {t("schedule.booked")}
        </span>
      );
    default:
      return null;
  }
}
